/**
 * Große Momente als Einblendung: Zeitungstitelseite bei Krisen, Affären, Koalitionsbruch und Wahlausgang,
 * Auszählung bei Abstimmungen im Bundestag. Reine Anzeige; beim Laden eines Spielstands erscheint nichts.
 */
import { useEffect, useRef, useState } from 'react';
import { useS } from '../../store/game';
import { CRISES } from '../../content/crises';
import { OUTLETS } from '../../content/parties';
import { RID } from '../../content/ressorts';
import { PA, PC, PN } from '../../engine/rules';
import { seatPoints } from '../../engine/view';
import { NEED } from '../../content/consts';
import { de, dateLabel } from '../../engine/util';
import { Illu } from '../../components/Illu';
import { track } from '../../lib/telemetry';

type Front = { kind: 'front'; key: string; head: string; sub: string; img: string; tone: 'bad' | 'good' };
type Vote = { kind: 'vote'; key: string; t: string; yes: number; ok: boolean; by: Record<string, boolean> };
type Moment = Front | Vote;

/** Welche Momente stecken gerade im Spielstand? */
function candidates(S: any): Moment[] {
  const out: Moment[] = [];
  if (!S || !S.phase) return out;
  if (S.crisis) { const C = CRISES.find((c: any) => c.id === S.crisis.id); if (C) out.push({ kind: 'front', key: `cr-${C.id}-${S.crisis.m}`, head: C.t, sub: C.s, img: `/img/krise/${C.id}.webp`, tone: 'bad' }); }
  if (S.scandal && S.min?.[S.scandal.rid]) { const M = S.min[S.scandal.rid]; out.push({ kind: 'front', key: `sc-${S.scandal.rid}-${S.scandal.m}`, head: S.scandal.t, sub: `${M.n} (${PA(M.p)}), zuständig für ${RID[S.scandal.rid]?.n ?? 'ein Ressort'}, gerät unter Druck. Die Opposition fordert den Rücktritt.`, img: '/img/moment/skandal.webp', tone: 'bad' }); }
  if (S.phase === 'end' && S.end) {
    const k = `end-${S.end}-${S.m}-${S.leg}`;
    if (S.end === 'coal') out.push({ kind: 'front', key: k, head: 'Die Koalition ist zerbrochen', sub: `Nach monatelangem Streit kündigt der Partner die Zusammenarbeit. ${S.name} steht ohne Mehrheit da, es gibt Neuwahlen.`, img: '/img/moment/koalitionsbruch.webp', tone: 'bad' });
    else if (S.end === 'misstrauen') out.push({ kind: 'front', key: k, head: 'Regierung per Misstrauensvotum gestürzt', sub: `Der Bundestag wählt eine neue Kanzlerin. Für ${S.name} endet die Regierungszeit abrupt.`, img: '/img/moment/misstrauen.webp', tone: 'bad' });
    else if (S.end === 'election') out.push(S.won
      ? { kind: 'front', key: k, head: `${S.name} bleibt an der Macht`, sub: `Mit ${de(S.final)} Prozent gewinnt ${PN('YOU')} die Bundestagswahl erneut.`, img: '/img/moment/wahlsieg.webp', tone: 'good' }
      : { kind: 'front', key: k, head: `Machtwechsel in Berlin`, sub: `${S.name} verliert die Wahl mit ${de(S.final)} Prozent und muss die Regierung abgeben.`, img: '/img/szene/ende.webp', tone: 'bad' });
  }
  const V = S.lastVote;
  if (V && S.phase === 'govern' && S.m - V.m <= 1) out.push({ kind: 'vote', key: `v-${V.id}`, t: V.t, yes: V.yes, ok: V.ok, by: V.by });
  return out;
}

const seen = new Set<string>();

export function Moments() {
  const S: any = useS();
  const first = useRef(true);
  const [cur, setCur] = useState<Moment | null>(null);
  const list = candidates(S);
  if (first.current) { first.current = false; list.forEach((m) => seen.add(m.key)); } // beim Laden nichts nachholen
  const next = cur ? null : list.find((m) => !seen.has(m.key)) ?? null;
  useEffect(() => { if (next) { seen.add(next.key); setCur(next); track('Moment.shown', { kind: next.kind === 'front' ? next.key.split('-')[0] : 'vote' }); } }, [next?.key]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!cur) return null;
  const close = () => setCur(null);
  return cur.kind === 'front' ? <FrontPage m={cur} date={dateLabel(S.m)} close={close} /> : <VoteReveal v={cur} close={close} />;
}

function Overlay({ children, close, label }: { children: React.ReactNode; close: () => void; label: string }) {
  const btn = useRef<HTMLButtonElement>(null);
  useEffect(() => { btn.current?.focus(); const k = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); }; window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k); }, [close]);
  return (
    <div className="moment-bg" onClick={close}>
      <div className="moment" role="dialog" aria-modal="true" aria-label={label} onClick={(e) => e.stopPropagation()}>
        {children}
        <div className="actions"><button ref={btn} type="button" className="yes" onClick={close}>Weiter</button></div>
      </div>
    </div>
  );
}

function FrontPage({ m, date, close }: { m: Front; date: string; close: () => void }) {
  const outlet = OUTLETS[m.tone === 'good' ? 1 : 0]?.n ?? 'Tagesbote';
  return (
    <Overlay close={close} label={`Titelseite: ${m.head}`}>
      <article className={'front ' + m.tone}>
        <header className="front-mast"><span>{date}</span><b>{outlet}</b><span>Sonderausgabe</span></header>
        <Illu src={m.img} className="illu front" />
        <h2 className="front-h">{m.head}</h2>
        <p className="front-s">{m.sub}</p>
      </article>
    </Overlay>
  );
}

const VDUR = 2200;
function VoteReveal({ v, close }: { v: Vote; close: () => void }) {
  const reduce = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const [p, setP] = useState(reduce ? 1 : 0);
  useEffect(() => {
    if (p >= 1) return;
    let raf = 0; const t0 = performance.now();
    const step = (t: number) => { const q = Math.min(1, (t - t0) / VDUR); setP(q); if (q < 1) raf = requestAnimationFrame(step); };
    raf = requestAnimationFrame(step); return () => cancelAnimationFrame(raf);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const pts = seatPoints();
  const k = Math.floor(p * pts.length), done = p >= 1;
  let y = 0; for (let i = 0; i < k; i++) if (v.by[pts[i].pid]) y++;
  const yes = done ? v.yes : Math.min(y, v.yes), no = done ? 630 - v.yes : k - Math.min(y, k);
  const budget = v.key.startsWith('v-hh');
  return (
    <Overlay close={close} label={`Abstimmung: ${v.t}`}>
      <span className="label">{budget ? 'Haushaltsabstimmung' : 'Namentliche Abstimmung'} im Bundestag</span>
      <h2>{v.t}</h2>
      <svg className="hemi vote" viewBox="-104 -4 208 110" role="img" aria-label={`${v.yes} Ja, ${630 - v.yes} Nein`}>
        {pts.map((pt: any, i: number) => <circle key={i} cx={(pt.x * 100).toFixed(1)} cy={(100 - pt.y * 100).toFixed(1)} r="1.7"
          fill={i < k ? (v.by[pt.pid] ? PC(pt.pid) : 'transparent') : 'var(--chip)'} stroke={i < k && !v.by[pt.pid] ? PC(pt.pid) : 'none'} strokeWidth=".6" opacity={i < k && !v.by[pt.pid] ? .55 : 1} />)}
        <text x="0" y="92" textAnchor="middle" className="pl-num">{yes} : {no}</text>
        <text x="0" y="102" textAnchor="middle" className="pl-cap">Ja : Nein · nötig {NEED}</text>
      </svg>
      <div className="vote-res" style={{ opacity: done ? 1 : 0 }}>
        <span className={'stamp ' + (v.ok ? 'ok' : 'no')}>{v.ok ? 'Angenommen' : 'Abgelehnt'}</span>
        <div className="tags">{Object.keys(v.by).map((id) => <span className={'tag ' + (v.by[id] ? 'up' : 'down')} key={id}><i className="sw" style={{ background: PC(id) }} />{PA(id)} {v.by[id] ? 'Ja' : 'Nein'}</span>)}</div>
      </div>
    </Overlay>
  );
}
